// TradeJournal.jsx — Trade Journal + Analysis Log tabs (light theme)
import React, { useCallback, useEffect, useState } from "react";
import PipelineScores from "../components/PipelineScores";
import TimerV2Setups from "../components/TimerV2Setups";

const API = process.env.REACT_APP_API_URL || "http://localhost:8000";
const PAGE_SIZE = 10;

const OUTCOME_COLOR = {
  WINNER:     "text-green-600",
  BIG_WINNER: "text-emerald-600",
  LOSER:      "text-red-600",
  EXPIRED:    "text-gray-400",
};

// Block stage badge — distinguishes where in the pipeline a signal was rejected.
// PRE_CLAUDE: blocked before Claude was ever called (score/production gates).
// CLAUDE_REJECTED: Claude ran but self-rejected (no_trade=true) or hit the R:R/invalid-math backstop.
// POST_CLAUDE: Claude returned a full valid trade plan; a downstream Python gate rejected it after.
function blockStageBadge(stage) {
  if (stage === "PRE_CLAUDE") {
    return <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-medium whitespace-nowrap">⛔ BLOCKED</span>;
  }
  if (stage === "CLAUDE_REJECTED") {
    return <span className="text-xs px-1.5 py-0.5 rounded bg-orange-100 text-orange-700 font-medium whitespace-nowrap">🚫 CLAUDE REJECTED</span>;
  }
  if (stage === "POST_CLAUDE") {
    return <span className="text-xs px-1.5 py-0.5 rounded bg-red-100 text-red-700 font-medium whitespace-nowrap">⛔ POST-CLAUDE</span>;
  }
  return null;
}

function fmt2(n) {
  return n != null ? parseFloat(n).toFixed(2) : "—";
}

function pctFrom(entry, target) {
  if (entry == null || target == null || entry === 0) return null;
  return ((target - entry) / entry * 100).toFixed(1);
}

function calcRR(entryLow, entryHigh, initialSl, sl, target1) {
  const em = entryLow != null && entryHigh != null
    ? (parseFloat(entryLow) + parseFloat(entryHigh)) / 2
    : entryLow != null ? parseFloat(entryLow) : null;
  const isl = initialSl != null ? parseFloat(initialSl) : (sl != null ? parseFloat(sl) : null);
  if (!em || !isl || isl >= em || target1 == null) return null;
  return ((parseFloat(target1) - em) / (em - isl)).toFixed(1);
}

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-IN", {
    month: "short", day: "2-digit",
    hour: "2-digit", minute: "2-digit",
    hour12: false,
  });
}

function fmtExpiry(isoDate) {
  if (!isoDate) return "—";
  const d = new Date(isoDate + "T00:00:00");
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "2-digit" });
}

// Extract SR level source + price from trigger_reason for SR_TOUCH/SR_BREAKOUT.
// "SUPPORT touch: Cluster (103x) (Cluster) @ 57399.1" -> "Cluster @ 57399"
function srLevelLabel(triggerReason) {
  if (!triggerReason) return null;
  const src   = (triggerReason.match(/\b(Cluster|Pivot|Swing|Round)\b/i) || [])[0];
  const price = (triggerReason.match(/@\s*([\d.]+)/) || [])[1];
  if (!src && !price) return null;
  return `${src || "SR"}${price ? ` @ ${Math.round(parseFloat(price))}` : ""}`;
}

// SWING breakout trigger detail shown under the trigger chip.
// Example: "SWING breakout: broke swing low @ 1420.7 at 14:36 IST"
function swingBreakLabel(triggerReason) {
  if (!triggerReason) return null;
  const side = (triggerReason.match(/broke\s+swing\s+(low|high)/i) || [])[1];
  const price = (triggerReason.match(/@\s*([\d.]+)/) || [])[1];
  const when = (triggerReason.match(/at\s+(\d{2}:\d{2})\s*IST/i) || [])[1];
  if (!side && !price && !when) return null;
  const sideTxt = side ? (side.toUpperCase() === "LOW" ? "Low" : "High") : "Level";
  const priceTxt = price ? ` @ ${Math.round(parseFloat(price))}` : "";
  const whenTxt = when ? ` · ${when}` : "";
  return `${sideTxt}${priceTxt}${whenTxt}`;
}

// TIMER v2 predictive-coil detail shown under the trigger chip.
// Example: "TIMER v2 coil: 15b squeeze, vol BUILDING, Q8.9 @ 584.98-585.42"
// Produces: "Q8.9 · 15b coil · building"
function timerV2Label(triggerReason) {
  if (!triggerReason) return null;
  const bars = (triggerReason.match(/(\d+)b\s+squeeze/i) || [])[1];
  const vol  = (triggerReason.match(/vol\s+(BUILDING|STEADY|FADING)/i) || [])[1];
  const q    = (triggerReason.match(/\bQ([\d.]+)/) || [])[1];
  if (!bars && !vol && !q) return null;
  return [
    q ? `Q${q}` : null,
    bars ? `${bars}b coil` : null,
    vol ? vol.toLowerCase() : null,
  ].filter(Boolean).join(" · ");
}

// A T1-hit signal still trailing is already a locked win (SL moved to breakeven),
// so it counts as WINNER — matching the Dashboard's rule and keeping the two views
// consistent.
function effOutcome(s) {
  return s.outcome || (s.t1_hit ? "WINNER" : null);
}

function StatsBar({ signals }) {
  const winners  = signals.filter(s => { const o = effOutcome(s); return o === "WINNER" || o === "BIG_WINNER"; }).length;
  const resolved = signals.filter(s => effOutcome(s)).length;
  const winRate  = resolved > 0 ? ((winners / resolved) * 100).toFixed(0) : null;
  const losers   = signals.filter(s => s.outcome === "LOSER").length;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
      {[
        { label: "Showing",  val: signals.length,                        color: "text-gray-700" },
        { label: "Win Rate", val: winRate != null ? `${winRate}%` : "—", color: "text-green-600" },
        { label: "Winners",  val: winners,                                color: "text-green-600" },
        { label: "Losers",   val: losers,                                 color: "text-red-600" },
      ].map((s) => (
        <div key={s.label} className="bg-white border border-gray-200 rounded-xl p-3">
          <p className="text-gray-400 text-xs">{s.label}</p>
          <p className={`text-2xl font-bold font-mono mt-1 ${s.color}`}>{s.val}</p>
        </div>
      ))}
    </div>
  );
}

const FILTERS = ["ALL", "WINNER", "BIG_WINNER", "LOSER", "EXPIRED", "pending"];

// Trigger-type options for the Journal + Analysis Log trigger filter. Values match
// Signal.trigger_type (see backend trigger_type_map).
const TRIGGER_TYPES = [
  "ALL", "TIMER", "TIMER_V2", "SR_TOUCH", "SR_BREAKOUT", "SWING_BREAKOUT",
  "ORB", "COMPRESSION_BREAKOUT", "TICK", "INTRADAY_SCALP", "EVENT", "NEWS",
];

function FilterRow({ filter, setFilter, dateFrom, setDateFrom, dateTo, setDateTo, outcomes, total, symbolSearch, setSymbolSearch, triggerFilter, setTriggerFilter }) {
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <select
        value={filter}
        onChange={e => setFilter(e.target.value)}
        className="text-xs bg-white text-gray-700 border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:border-blue-500"
      >
        {FILTERS.map(f => (
          <option key={f} value={f}>
            {f === "ALL"
              ? `All (${total})`
              : f === "pending"
              ? `Pending (${outcomes.pending || 0})`
              : `${f} (${outcomes[f] || 0})`}
          </option>
        ))}
      </select>
      <select
        value={triggerFilter}
        onChange={e => setTriggerFilter(e.target.value)}
        className="text-xs bg-white text-gray-700 border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:border-blue-500"
      >
        {TRIGGER_TYPES.map(t => (
          <option key={t} value={t}>{t === "ALL" ? "All triggers" : t}</option>
        ))}
      </select>
      <input
        type="text"
        value={symbolSearch}
        onChange={e => setSymbolSearch(e.target.value)}
        placeholder="Symbol…"
        className="bg-white text-gray-700 border border-gray-300 rounded-lg px-2 py-1.5 text-xs w-24 focus:outline-none focus:border-blue-500"
      />
      <input
        type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
        placeholder="From date"
        className="bg-white text-gray-700 border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-blue-500"
      />
      <input
        type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
        placeholder="To date"
        className="bg-white text-gray-700 border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-blue-500"
      />
      {(dateFrom || dateTo || symbolSearch || (triggerFilter && triggerFilter !== "ALL")) && (
        <button
          onClick={() => { setDateFrom(""); setDateTo(""); setSymbolSearch && setSymbolSearch(""); setTriggerFilter && setTriggerFilter("ALL"); }}
          className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
        >
          ✕ Clear
        </button>
      )}
    </div>
  );
}

function Pagination({ page, setPage, total }) {
  const pages = Math.ceil(total / PAGE_SIZE);
  if (pages <= 1) return null;
  return (
    <div className="flex items-center justify-between mt-3">
      <button
        disabled={page === 0}
        onClick={() => setPage(p => p - 1)}
        className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 disabled:opacity-40 hover:bg-gray-50"
      >Prev</button>
      <span className="text-xs text-gray-400">{page + 1} / {pages}</span>
      <button
        disabled={page >= pages - 1}
        onClick={() => setPage(p => p + 1)}
        className="text-xs px-3 py-1.5 rounded-lg border border-gray-200 text-gray-600 disabled:opacity-40 hover:bg-gray-50"
      >Next</button>
    </div>
  );
}

function SingleLegTable({ signals, loading, showReason }) {
  const [page, setPage] = useState(0);
  const rows = signals.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  if (loading) return <div className="h-40 bg-gray-100 rounded-xl animate-pulse" />;
  if (signals.length === 0) return (
    <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-400">
      No records match this filter
    </div>
  );
  return (
    <>
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-400 border-b border-gray-200 bg-gray-50">
                {["Time", "Instrument", "Strike", "Dir", "Expiry", "Strategy", ...(showReason ? ["Reason"] : []), "Trigger", "Score", "Entry", "SL", "T1", "T2", "R:R", "Outcome", "Return", "Min"].map(h => (
                  <th key={h} className="px-3 py-2 text-left font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(s => (
                <tr key={s.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <td className="px-3 py-2 text-gray-400 whitespace-nowrap">{fmtDate(s.created_at)}</td>
                  <td className="px-3 py-2 font-medium text-gray-800 whitespace-nowrap">{s.instrument}</td>
                  <td className="px-3 py-2 text-gray-600 font-mono">{s.strike ? `${s.strike} ${s.option_type ?? ""}`.trim() : "—"}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {(() => {
                      const dir = s.direction || (s.option_type === "CE" ? "BULLISH" : s.option_type === "PE" ? "BEARISH" : null);
                      return dir === "BULLISH"
                        ? <span className="font-bold text-green-600">▲ Bull</span>
                        : dir === "BEARISH"
                        ? <span className="font-bold text-red-600">▼ Bear</span>
                        : dir === "NEUTRAL"
                        ? <span className="text-xs text-gray-500">NEUT</span>
                        : <span className="text-gray-300">—</span>;
                    })()}
                  </td>
                  <td className="px-3 py-2 text-gray-500 whitespace-nowrap">{s.expiry ? fmtExpiry(s.expiry) : "—"}</td>
                  <td className="px-3 py-2">
                    {s.block_stage
                      ? blockStageBadge(s.block_stage)
                      : s.strategy_type === "NO_TRADE"
                      ? <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 font-medium whitespace-nowrap">📊 Raw log</span>
                      : <span className="text-gray-500">{s.strategy_type ?? "—"}</span>
                    }
                  </td>
                  {showReason && (
                    <td className="px-3 py-2" style={{minWidth: 200, maxWidth: 300}}>
                      {s.block_stage
                        ? <span className="text-xs text-red-600 leading-tight">{(s.trigger_reason || "").replace(/^\[BLOCKED\]\s*/, "")}</span>
                        : s.claude_reasoning
                        ? <span className="text-xs text-gray-500 leading-tight">{s.claude_reasoning}</span>
                        : <span className="text-gray-300">—</span>
                      }
                    </td>
                  )}
                  <td className="px-3 py-2 whitespace-nowrap">
                    {s.trigger_type === "COMPRESSION_BREAKOUT"
                      ? <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">🚀 COMPRESSION</span>
                      : s.trigger_type === "TIMER_V2"
                      ? <div>
                          <span className="text-xs px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-700 font-medium">🌀 TIMER-V2</span>
                          {timerV2Label(s.trigger_reason) && (
                            <span className="block text-cyan-600 text-xs mt-0.5">{timerV2Label(s.trigger_reason)}</span>
                          )}
                        </div>
                      : (s.trigger_type === "SR_TOUCH" || s.trigger_type === "SR_BREAKOUT")
                      ? <div>
                          <span className="text-xs px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 font-medium">
                            {s.trigger_type === "SR_TOUCH" ? "📍 SR" : "🔷 SR-BO"}
                          </span>
                          {srLevelLabel(s.trigger_reason) && (
                            <span className="block text-violet-500 text-xs mt-0.5">{srLevelLabel(s.trigger_reason)}</span>
                          )}
                        </div>
                      : s.trigger_type === "SWING_BREAKOUT"
                      ? <div>
                          <span className="text-xs px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-medium">📉 SWING-BO</span>
                          {swingBreakLabel(s.trigger_reason) && (
                            <span className="block text-indigo-500 text-xs mt-0.5">{swingBreakLabel(s.trigger_reason)}</span>
                          )}
                        </div>
                      : <span className="text-gray-400">{s.trigger_type ?? "—"}</span>}
                  </td>
                  <td className="px-3 py-2 font-mono">
                    {(() => {
                      const isSr = s.trigger_type === "SR_TOUCH" || s.trigger_type === "SR_BREAKOUT";
                      const thr  = isSr ? 7.5 : s.instrument_type === "STOCK" ? 9.0 : 7.5;
                      const warn = isSr ? 6.5 : s.instrument_type === "STOCK" ? 8.0 : 6.5;
                      return (
                        <span className={s.score >= thr ? "text-green-600" : s.score >= warn ? "text-yellow-600" : "text-gray-400"}>
                          {s.score?.toFixed(1) ?? "—"}
                        </span>
                      );
                    })()}
                  </td>
                  <td className="px-3 py-2 text-gray-700 font-mono">{fmt2(s.entry_low)}</td>
                  <td className="px-3 py-2 font-mono">
                    <span className="text-red-600">{fmt2(s.stop_loss)}</span>
                    {pctFrom(s.entry_low, s.stop_loss) != null && (
                      <span className="block text-red-400 text-xs">{pctFrom(s.entry_low, s.stop_loss)}%</span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono">
                    <span className="text-green-600">{fmt2(s.target_1)}</span>
                    {pctFrom(s.entry_low, s.target_1) != null && (
                      <span className="block text-green-500 text-xs">+{pctFrom(s.entry_low, s.target_1)}%</span>
                    )}
                  </td>
                  <td className="px-3 py-2 font-mono">
                    <span className="text-emerald-600">{fmt2(s.target_2)}</span>
                    {pctFrom(s.entry_low, s.target_2) != null && (
                      <span className="block text-emerald-500 text-xs">+{pctFrom(s.entry_low, s.target_2)}%</span>
                    )}
                  </td>
                  {(() => {
                    const rr = !s.t1_hit ? calcRR(s.entry_low, s.entry_high, s.initial_stop_loss, s.stop_loss, s.target_1) : null;
                    return (
                      <td className="px-3 py-2 font-mono">
                        {rr ? <span className="text-blue-600">1:{rr}</span> : <span className="text-gray-300">—</span>}
                      </td>
                    );
                  })()}
                  <td className={`px-3 py-2 font-medium ${OUTCOME_COLOR[s.outcome] || "text-yellow-600"}`}>
                    {s.outcome ?? (s.t1_hit ? "T1✓ trailing" : "pending")}
                  </td>
                  <td className={`px-3 py-2 font-mono ${s.return_pct >= 0 ? "text-green-600" : "text-red-600"}`}>
                    {s.return_pct != null ? `${s.return_pct >= 0 ? "+" : ""}${s.return_pct.toFixed(1)}%` : "—"}
                  </td>
                  <td className="px-3 py-2 text-gray-400 font-mono">{s.minutes_to_outcome ?? "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <Pagination page={page} setPage={setPage} total={signals.length} />
    </>
  );
}

function MultiLegTable({ signals, loading, showReason }) {
  const [page, setPage] = useState(0);
  const rows = signals.slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE);

  if (loading) return <div className="h-40 bg-gray-100 rounded-xl animate-pulse" />;
  if (signals.length === 0) return (
    <div className="bg-white border border-gray-200 rounded-xl p-8 text-center text-gray-400">
      No multi-leg signals recorded yet
    </div>
  );
  return (
    <>
      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-400 border-b border-gray-200 bg-gray-50">
                {["Time", "Symbol", "Trigger", "Dir/Strategy", ...(showReason ? ["Reason"] : []), "Legs", "Net Rcvd", "SL", "T1", "T2", "Max P", "Max L", "Outcome", "Return"].map(h => (
                  <th key={h} className="px-3 py-2 text-left font-medium">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map(s => (
                <tr key={s.id} className="border-b border-gray-100 hover:bg-gray-50 transition-colors">
                  <td className="px-3 py-2 text-gray-400 whitespace-nowrap">{fmtDate(s.created_at)}</td>
                  <td className="px-3 py-2 font-medium text-gray-800">{s.instrument}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {s.trigger_type === "COMPRESSION_BREAKOUT"
                      ? <span className="text-xs px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 font-medium">🚀 COMPRESSION</span>
                      : s.trigger_type === "TIMER_V2"
                      ? <div>
                          <span className="text-xs px-1.5 py-0.5 rounded bg-cyan-100 text-cyan-700 font-medium">🌀 TIMER-V2</span>
                          {timerV2Label(s.trigger_reason) && (
                            <span className="block text-cyan-600 text-xs mt-0.5">{timerV2Label(s.trigger_reason)}</span>
                          )}
                        </div>
                      : (s.trigger_type === "SR_TOUCH" || s.trigger_type === "SR_BREAKOUT")
                      ? <div>
                          <span className="text-xs px-1.5 py-0.5 rounded bg-violet-100 text-violet-700 font-medium">
                            {s.trigger_type === "SR_TOUCH" ? "📍 SR" : "🔷 SR-BO"}
                          </span>
                          {srLevelLabel(s.trigger_reason) && (
                            <span className="block text-violet-500 text-xs mt-0.5">{srLevelLabel(s.trigger_reason)}</span>
                          )}
                        </div>
                      : s.trigger_type === "SWING_BREAKOUT"
                      ? <div>
                          <span className="text-xs px-1.5 py-0.5 rounded bg-indigo-100 text-indigo-700 font-medium">📉 SWING-BO</span>
                          {swingBreakLabel(s.trigger_reason) && (
                            <span className="block text-indigo-500 text-xs mt-0.5">{swingBreakLabel(s.trigger_reason)}</span>
                          )}
                        </div>
                      : <span className="text-gray-400">{s.trigger_type ?? "—"}</span>}
                  </td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {s.direction === "BULLISH"
                      ? <span className="block font-bold text-green-600 text-xs">▲ Bull</span>
                      : s.direction === "BEARISH"
                      ? <span className="block font-bold text-red-600 text-xs">▼ Bear</span>
                      : s.direction === "NEUTRAL"
                      ? <span className="block text-gray-500 text-xs">NEUT</span>
                      : null}
                    {s.block_stage
                      ? (
                        <div>
                          {blockStageBadge(s.block_stage)}
                        </div>
                      )
                      : <span className="text-xs px-1.5 py-0.5 rounded bg-sky-100 text-sky-700 font-medium">
                          {s.strategy_type ?? "—"}
                        </span>
                    }
                  </td>
                  {showReason && (
                    <td className="px-3 py-2" style={{minWidth: 200, maxWidth: 300}}>
                      {s.block_stage
                        ? <span className="text-xs text-red-600 leading-tight">{(s.trigger_reason || "").replace(/^\[BLOCKED\]\s*/, "")}</span>
                        : s.claude_reasoning
                        ? <span className="text-xs text-gray-500 leading-tight">{s.claude_reasoning}</span>
                        : <span className="text-gray-300">—</span>
                      }
                    </td>
                  )}
                  <td className="px-3 py-2 font-mono text-gray-600">
                    {(s.legs || []).map((leg, i) => (
                      <div key={i} className="whitespace-nowrap">
                        <span className={leg.action === "BUY" ? "text-green-600 font-bold" : "text-red-600 font-bold"}>{leg.action ?? "BUY"}</span>
                        {" "}<span className="text-gray-700">{leg.instrument ?? `${leg.strike} ${leg.option_type ?? ""}`}</span>
                        {leg.premium != null && <span className="text-gray-400"> @₹{fmt2(leg.premium)}</span>}
                      </div>
                    ))}
                  </td>
                  <td className="px-3 py-2 font-mono">
                    {s.net_premium != null
                      ? <span className={parseFloat(s.net_premium) < 0 ? "text-purple-600 font-semibold" : "text-gray-700"}>
                          {parseFloat(s.net_premium) < 0 ? `₹${Math.abs(s.net_premium).toFixed(2)} rcvd` : `₹${fmt2(s.net_premium)}`}
                        </span>
                      : "—"}
                  </td>
                  <td className="px-3 py-2 font-mono text-red-600">{fmt2(s.stop_loss)}</td>
                  <td className="px-3 py-2 font-mono text-green-600">{fmt2(s.target_1)}</td>
                  <td className="px-3 py-2 font-mono text-emerald-600">{fmt2(s.target_2)}</td>
                  <td className="px-3 py-2 font-mono text-green-600">{s.max_profit != null ? `₹${fmt2(s.max_profit)}` : "—"}</td>
                  <td className="px-3 py-2 font-mono text-red-600">{s.max_loss != null ? `₹${fmt2(s.max_loss)}` : "—"}</td>
                  <td className={`px-3 py-2 font-medium ${OUTCOME_COLOR[s.outcome] || "text-yellow-600"}`}>
                    {s.outcome ?? "pending"}
                  </td>
                  <td className={`px-3 py-2 font-mono ${s.return_pct >= 0 ? "text-green-600" : "text-red-600"}`}>
                    {s.return_pct != null ? `${s.return_pct >= 0 ? "+" : ""}${s.return_pct.toFixed(1)}%` : "—"}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <Pagination page={page} setPage={setPage} total={signals.length} />
    </>
  );
}

function SignalTable({ signals, loading, showReason }) {
  const [legTab, setLegTab] = useState("single");
  const single = signals.filter(s => !s.legs || s.legs.length <= 1);
  const multi  = signals.filter(s => s.legs && s.legs.length > 1);

  return (
    <div>
      {/* Single/Multi sub-tabs */}
      <div className="flex gap-1 mb-3">
        <button
          onClick={() => setLegTab("single")}
          className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${
            legTab === "single"
              ? "bg-blue-600 text-white"
              : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
          }`}
        >
          Single Leg ({single.length})
        </button>
        <button
          onClick={() => setLegTab("multi")}
          className={`px-3 py-1.5 text-xs rounded-lg font-medium transition-colors ${
            legTab === "multi"
              ? "bg-blue-600 text-white"
              : "bg-white border border-gray-200 text-gray-600 hover:bg-gray-50"
          }`}
        >
          Multi Leg ({multi.length})
        </button>
      </div>
      {legTab === "single"
        ? <SingleLegTable signals={single} loading={loading} showReason={showReason} />
        : <MultiLegTable  signals={multi}  loading={loading} showReason={showReason} />}
    </div>
  );
}

function applyFilter(rows, filter, dateFrom, dateTo, typeFilter, symbolSearch, triggerFilter) {
  const sym = (symbolSearch || "").trim().toUpperCase();
  return rows.filter(s => {
    if (filter !== "ALL") {
      const eff = effOutcome(s);
      if (filter === "pending" && eff) return false;
      if (filter !== "pending" && eff !== filter) return false;
    }
    if (dateFrom || dateTo) {
      const dt = new Date(s.created_at);
      if (dateFrom && dt < new Date(dateFrom)) return false;
      if (dateTo && dt > new Date(dateTo + "T23:59:59")) return false;
    }
    if (typeFilter && typeFilter !== "ALL") {
      if (typeFilter === "PRE_CLAUDE"      && s.block_stage !== "PRE_CLAUDE")      return false;
      if (typeFilter === "CLAUDE_REJECTED" && s.block_stage !== "CLAUDE_REJECTED") return false;
      if (typeFilter === "POST_CLAUDE"     && s.block_stage !== "POST_CLAUDE")     return false;
      if (typeFilter === "BELOW_THRESHOLD" && s.strategy_type !== "NO_TRADE") return false;
      if (typeFilter === "CLAUDE" && (s.block_stage != null || s.strategy_type === "NO_TRADE")) return false;
    }
    if (triggerFilter && triggerFilter !== "ALL") {
      if ((s.trigger_type || "").toUpperCase() !== triggerFilter) return false;
    }
    if (sym && !(s.instrument || "").toUpperCase().includes(sym)) return false;
    return true;
  });
}

function AnalysisFilterRow({ filter, setFilter, dateFrom, setDateFrom, dateTo, setDateTo,
  outcomes, total, typeFilter, setTypeFilter, symbolSearch, setSymbolSearch, triggerFilter, setTriggerFilter }) {
  const typeOptions = [
    { value: "ALL",             label: "All types" },
    { value: "PRE_CLAUDE",      label: "Blocked — Pre-Claude" },
    { value: "CLAUDE_REJECTED", label: "Blocked — Claude Rejected" },
    { value: "POST_CLAUDE",     label: "Blocked — Post-Claude Gate" },
    { value: "BELOW_THRESHOLD", label: "Below Threshold" },
    { value: "CLAUDE",          label: "Claude Analyzed" },
  ];
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <select
        value={filter}
        onChange={e => setFilter(e.target.value)}
        className="text-xs bg-white text-gray-700 border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:border-blue-500"
      >
        {FILTERS.map(f => (
          <option key={f} value={f}>
            {f === "ALL" ? `All outcomes (${total})` : f === "pending" ? `Pending (${outcomes.pending || 0})` : `${f} (${outcomes[f] || 0})`}
          </option>
        ))}
      </select>
      <select
        value={typeFilter}
        onChange={e => setTypeFilter(e.target.value)}
        className="text-xs bg-white text-gray-700 border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:border-blue-500"
      >
        {typeOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
      <select
        value={triggerFilter}
        onChange={e => setTriggerFilter(e.target.value)}
        className="text-xs bg-white text-gray-700 border border-gray-300 rounded-lg px-3 py-1.5 focus:outline-none focus:border-blue-500"
      >
        {TRIGGER_TYPES.map(t => (
          <option key={t} value={t}>{t === "ALL" ? "All triggers" : t}</option>
        ))}
      </select>
      <input
        type="text"
        value={symbolSearch}
        onChange={e => setSymbolSearch(e.target.value)}
        placeholder="Symbol…"
        className="bg-white text-gray-700 border border-gray-300 rounded-lg px-2 py-1.5 text-xs w-24 focus:outline-none focus:border-blue-500"
      />
      <input
        type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)}
        className="bg-white text-gray-700 border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-blue-500"
      />
      <input
        type="date" value={dateTo} onChange={e => setDateTo(e.target.value)}
        className="bg-white text-gray-700 border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-blue-500"
      />
      {(dateFrom || dateTo || symbolSearch || typeFilter !== "ALL" || filter !== "ALL" || (triggerFilter && triggerFilter !== "ALL")) && (
        <button
          onClick={() => { setDateFrom(""); setDateTo(""); setSymbolSearch(""); setTypeFilter("ALL"); setFilter("ALL"); setTriggerFilter && setTriggerFilter("ALL"); }}
          className="text-xs text-gray-400 hover:text-gray-600 transition-colors"
        >
          ✕ Clear
        </button>
      )}
    </div>
  );
}

function outcomeBadges(rows) {
  return rows.reduce((acc, s) => {
    const k = effOutcome(s) || "pending";
    acc[k] = (acc[k] || 0) + 1;
    return acc;
  }, {});
}

// ── Forward-lean Shadow A/B panel ────────────────────────────────────────────
const WINNER_STYLE = {
  SHADOW:  "bg-purple-100 text-purple-700",
  LIVE:    "bg-blue-100 text-blue-700",
  TIE:     "bg-gray-100 text-gray-600",
  PENDING: "bg-yellow-100 text-yellow-700",
};

function shadowLegLabel(row) {
  if (!row) return "—";
  const opt = row.option_type || (row.legs && row.legs[0] && row.legs[0].option_type) || "";
  const strike = row.strike || (row.legs && row.legs[0] && row.legs[0].strike) || "";
  return `${row.direction || ""} ${strike}${opt}`.trim();
}

function retCell(row) {
  const r = row && row.return_pct;
  if (r == null) return <span className="text-yellow-600">pending</span>;
  const cls = r > 0 ? "text-green-600" : r < 0 ? "text-red-600" : "text-gray-500";
  return <span className={cls}>{r > 0 ? "+" : ""}{parseFloat(r).toFixed(1)}%</span>;
}

function priceCell(v) {
  return v != null ? <span className="font-mono text-gray-700">{fmt2(v)}</span> : <span className="text-gray-300">—</span>;
}

function ShadowPanel({ pairs, summary, perTrigger, loading }) {
  if (loading) {
    return <div className="text-center text-gray-400 py-10">Loading shadow A/B…</div>;
  }
  const s = summary || {};
  const decided = (s.shadow_wins || 0) + (s.live_wins || 0);
  const cards = [
    { label: "Pairs",          val: s.total || 0,                                            color: "text-gray-700" },
    { label: "Engine wins",    val: s.shadow_wins || 0,                                      color: "text-purple-600" },
    { label: "Live wins",      val: s.live_wins || 0,                                        color: "text-blue-600" },
    { label: "Engine win %",   val: s.shadow_win_pct != null ? `${s.shadow_win_pct}%` : "—", color: "text-purple-600" },
  ];
  return (
    <>
      <div className="bg-purple-50 border border-purple-200 rounded-lg px-4 py-2 text-xs text-purple-700">
        Forward-lean SHADOW A/B — when anticipation DISAGREES with a live signal, a non-alerted twin trade is
        recorded for head-to-head comparison. "Engine win %" = how often the new anticipation
        layer beats the current system on realized return. No alerts sent; live signals are unchanged.
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {cards.map((c) => (
          <div key={c.label} className="bg-white border border-gray-200 rounded-xl p-3">
            <p className="text-gray-400 text-xs">{c.label}</p>
            <p className={`text-2xl font-bold font-mono mt-1 ${c.color}`}>{c.val}</p>
          </div>
        ))}
      </div>

      {perTrigger && Object.keys(perTrigger).length > 0 && (
        <div className="bg-white border border-gray-200 rounded-xl p-3 overflow-x-auto">
          <p className="text-gray-500 text-xs font-medium mb-2">Per-trigger breakdown</p>
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-400 text-left border-b border-gray-100">
                <th className="px-2 py-1">Trigger</th>
                <th className="px-2 py-1">Pairs</th>
                <th className="px-2 py-1 text-purple-600">Engine</th>
                <th className="px-2 py-1 text-blue-600">Live</th>
                <th className="px-2 py-1">Tie</th>
                <th className="px-2 py-1">Pending</th>
                <th className="px-2 py-1">Engine %</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(perTrigger).map(([trig, t]) => {
                const dec = (t.shadow_wins || 0) + (t.live_wins || 0);
                const pct = dec ? Math.round((t.shadow_wins / dec) * 100) : null;
                return (
                  <tr key={trig} className="border-b border-gray-50">
                    <td className="px-2 py-1 font-medium text-gray-700">{trig}</td>
                    <td className="px-2 py-1 font-mono">{t.total}</td>
                    <td className="px-2 py-1 font-mono text-purple-600">{t.shadow_wins}</td>
                    <td className="px-2 py-1 font-mono text-blue-600">{t.live_wins}</td>
                    <td className="px-2 py-1 font-mono text-gray-500">{t.tie}</td>
                    <td className="px-2 py-1 font-mono text-yellow-600">{t.pending}</td>
                    <td className="px-2 py-1 font-mono">{pct != null ? `${pct}%` : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      <div className="bg-white border border-gray-200 rounded-xl overflow-x-auto">
        <table className="w-full text-xs">
          <thead>
            <tr className="text-gray-400 text-left border-b border-gray-100">
              <th className="px-3 py-2">Time</th>
              <th className="px-3 py-2">Trigger</th>
              <th className="px-3 py-2">Instrument</th>
              <th className="px-3 py-2 text-blue-600">LIVE (current)</th>
              <th className="px-3 py-2 text-blue-600">Live Entry</th>
              <th className="px-3 py-2 text-blue-600">Live SL</th>
              <th className="px-3 py-2 text-blue-600">Live T1</th>
              <th className="px-3 py-2 text-blue-600">Live T2</th>
              <th className="px-3 py-2 text-blue-600">Live ret</th>
              <th className="px-3 py-2 text-purple-600">SHADOW (engine)</th>
              <th className="px-3 py-2 text-purple-600">Shadow Entry</th>
              <th className="px-3 py-2 text-purple-600">Shadow SL</th>
              <th className="px-3 py-2 text-purple-600">Shadow T1</th>
              <th className="px-3 py-2 text-purple-600">Shadow T2</th>
              <th className="px-3 py-2 text-purple-600">Shadow ret</th>
              <th className="px-3 py-2">Winner</th>
            </tr>
          </thead>
          <tbody>
            {pairs.length === 0 && (
              <tr><td colSpan={16} className="px-3 py-6 text-center text-gray-400">
                No shadow A/B pairs recorded yet.
              </td></tr>
            )}
            {pairs.map((p) => (
              <tr key={p.shadow_group} className="border-b border-gray-50 hover:bg-gray-50">
                <td className="px-3 py-2 text-gray-500 whitespace-nowrap">
                  {p.created_at ? new Date(p.created_at).toLocaleString() : "—"}
                </td>
                <td className="px-3 py-2 font-medium text-gray-700">{p.trigger_type}</td>
                <td className="px-3 py-2">{p.instrument}</td>
                <td className="px-3 py-2 text-blue-700">{shadowLegLabel(p.live)}</td>
                <td className="px-3 py-2">{priceCell(p.live && p.live.entry_low)}</td>
                <td className="px-3 py-2">{priceCell(p.live && p.live.stop_loss)}</td>
                <td className="px-3 py-2">{priceCell(p.live && p.live.target_1)}</td>
                <td className="px-3 py-2">{priceCell(p.live && p.live.target_2)}</td>
                <td className="px-3 py-2 font-mono">{retCell(p.live)}</td>
                <td className="px-3 py-2 text-purple-700">{shadowLegLabel(p.shadow)}</td>
                <td className="px-3 py-2">{priceCell(p.shadow && p.shadow.entry_low)}</td>
                <td className="px-3 py-2">{priceCell(p.shadow && p.shadow.stop_loss)}</td>
                <td className="px-3 py-2">{priceCell(p.shadow && p.shadow.target_1)}</td>
                <td className="px-3 py-2">{priceCell(p.shadow && p.shadow.target_2)}</td>
                <td className="px-3 py-2 font-mono">{retCell(p.shadow)}</td>
                <td className="px-3 py-2">
                  <span className={`px-2 py-0.5 rounded font-medium ${WINNER_STYLE[p.winner] || "bg-gray-100 text-gray-600"}`}>
                    {p.winner === "SHADOW" ? "ENGINE" : p.winner}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function PivotLevelsTable({ pivots, watchlist, loading }) {
  const allSymbols = ["NIFTY", "BANKNIFTY", ...(watchlist || [])];
  const rows = allSymbols.filter(s => pivots?.[s]?.levels);

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-600 uppercase tracking-wider">
          Daily Pivot Levels
        </span>
        <span className="text-xs text-gray-400">{rows.length} symbols</span>
      </div>
      {loading ? (
        <div className="py-6 text-center text-xs text-gray-400">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="py-6 text-center text-xs text-gray-400">No cached pivots.</div>
      ) : (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-400 border-b border-gray-100">
                <th className="text-left py-1">Symbol</th>
                <th className="text-right py-1">PP</th>
                <th className="text-right py-1">R1</th>
                <th className="text-right py-1">R2</th>
                <th className="text-right py-1">R3</th>
                <th className="text-right py-1">S1</th>
                <th className="text-right py-1">S2</th>
                <th className="text-right py-1">S3</th>
                <th className="text-right py-1">Date</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(sym => {
                const lvl = pivots[sym].levels;
                return (
                  <tr key={sym} className="border-b border-gray-50 last:border-b-0 text-gray-600 font-mono">
                    <td className="py-1 text-gray-700 font-semibold">{sym}</td>
                    <td className="py-1 text-right">{fmt2(lvl.pivot)}</td>
                    <td className="py-1 text-right text-green-600">{fmt2(lvl.r1)}</td>
                    <td className="py-1 text-right text-green-500">{fmt2(lvl.r2)}</td>
                    <td className="py-1 text-right text-green-400">{fmt2(lvl.r3)}</td>
                    <td className="py-1 text-right text-red-600">{fmt2(lvl.s1)}</td>
                    <td className="py-1 text-right text-red-500">{fmt2(lvl.s2)}</td>
                    <td className="py-1 text-right text-red-400">{fmt2(lvl.s3)}</td>
                    <td className="py-1 text-right text-gray-400">{pivots[sym].cache_date || "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function anchorTime(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts * 1000).toLocaleTimeString("en-IN", {
      hour: "2-digit", minute: "2-digit", timeZone: "Asia/Kolkata",
    });
  } catch (_) {
    return "—";
  }
}

function StateBadge({ side }) {
  if (!side) return <span className="text-gray-300">—</span>;
  const broken = side.state === "BROKEN";
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-semibold ${
      broken ? "bg-gray-100 text-gray-500" : "bg-green-50 text-green-600"
    }`}>
      {broken ? "BROKEN" : "ACTIVE"}
    </span>
  );
}

function SwingLevelsTable({ levels, symbols, loading }) {
  const rows = (symbols || []).filter(s => levels?.[s]);

  return (
    <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-3">
      <div className="flex items-center justify-between">
        <span className="text-sm font-semibold text-gray-600 uppercase tracking-wider">
          Swing Levels — Active / Broken
        </span>
        <span className="text-xs text-gray-400">{rows.length} symbols</span>
      </div>
      <div className="mt-1 text-[11px] text-gray-400">
        One live level per side. Anchored level is what the breakout doorbell monitors;
        "last seen" is the most recent recompute.
      </div>
      {loading ? (
        <div className="py-6 text-center text-xs text-gray-400">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="py-6 text-center text-xs text-gray-400">No swing levels cached.</div>
      ) : (
        <div className="mt-2 overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="text-gray-400 border-b border-gray-100">
                <th className="text-left py-1">Symbol</th>
                <th className="text-right py-1">Swing Low</th>
                <th className="text-center py-1">State</th>
                <th className="text-right py-1">Since</th>
                <th className="text-right py-1">Swing High</th>
                <th className="text-center py-1">State</th>
                <th className="text-right py-1">Since</th>
                <th className="text-right py-1">Last Seen Low</th>
                <th className="text-right py-1">Last Seen High</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(sym => {
                const d = levels[sym];
                return (
                  <tr key={sym} className="border-b border-gray-50 last:border-b-0 text-gray-600 font-mono">
                    <td className="py-1 text-gray-700 font-semibold">{sym}</td>
                    <td className="py-1 text-right text-red-600">{fmt2(d.low?.price)}</td>
                    <td className="py-1 text-center"><StateBadge side={d.low} /></td>
                    <td className="py-1 text-right text-gray-400">{anchorTime(d.low?.ts)}</td>
                    <td className="py-1 text-right text-green-600">{fmt2(d.high?.price)}</td>
                    <td className="py-1 text-center"><StateBadge side={d.high} /></td>
                    <td className="py-1 text-right text-gray-400">{anchorTime(d.high?.ts)}</td>
                    <td className="py-1 text-right text-gray-400">{fmt2(d.nearest_swing_low)}</td>
                    <td className="py-1 text-right text-gray-400">{fmt2(d.nearest_swing_high)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default function TradeJournal() {
  const [activeTab, setActiveTab] = useState("journal");
  // Trade Journal (alert_sent=True)
  const [signals, setSignals]     = useState([]);
  const [loadingJ, setLoadingJ]   = useState(true);
  const [filterJ, setFilterJ]     = useState("ALL");
  const [dateFromJ, setDateFromJ] = useState("");
  const [dateToJ, setDateToJ]     = useState("");
  const [symbolSearchJ, setSymbolSearchJ] = useState("");
  const [triggerFilterJ, setTriggerFilterJ] = useState("ALL");

  // Analysis Log (alert_sent=False — shadow tracked)
  const [analysisLog, setAnalysisLog]   = useState([]);
  const [loadingA, setLoadingA]         = useState(false);
  const [analysisLoaded, setAnalysisLoaded] = useState(false);
  const [filterA, setFilterA]           = useState("ALL");
  const [dateFromA, setDateFromA]       = useState("");
  const [dateToA, setDateToA]           = useState("");
  const [typeFilterA, setTypeFilterA]   = useState("ALL");
  const [symbolSearchA, setSymbolSearchA] = useState("");
  const [triggerFilterA, setTriggerFilterA] = useState("ALL");
  const [pipelineScores, setPipelineScores] = useState([]);
  // Timer v2 coil setups. Separate feed from pipelineScores because pipeline:score:*
  // only gets a row when a trigger FIRES — HOLD states are invisible there.
  const [timerV2Setups, setTimerV2Setups]   = useState([]);
  const [timerV2Counts, setTimerV2Counts]   = useState({ hold: 0, fired: 0 });

  // Shadow A/B (forward-lean engine vs live)
  const [shadowPairs, setShadowPairs]       = useState([]);
  const [shadowSummary, setShadowSummary]   = useState({});
  const [shadowPerTrigger, setShadowPerTrigger] = useState({});
  const [loadingS, setLoadingS]             = useState(false);
  const [shadowLoaded, setShadowLoaded]     = useState(false);

  // Levels (daily pivots + live swing anchors)
  const [pivots, setPivots]           = useState({});
  const [pivotWatchlist, setPivotWatchlist] = useState([]);
  const [swingLevels, setSwingLevels] = useState({});
  const [swingSymbols, setSwingSymbols] = useState([]);
  const [loadingL, setLoadingL]       = useState(false);
  const [levelsLoaded, setLevelsLoaded] = useState(false);

  // Fetch Trade Journal on mount
  useEffect(() => {
    fetch(`${API}/api/signals?limit=500`)
      .then((r) => r.json())
      .then((d) => setSignals(d.signals || []))
      .catch(() => {})
      .finally(() => setLoadingJ(false));
  }, []);

  // Fetch analysis log + pipeline scores (called on tab open and on every SSE event).
  const fetchAnalysis = useCallback(async (showSpinner) => {
    if (showSpinner) setLoadingA(true);
    try {
      const [logRes, psRes, tv2Res] = await Promise.all([
        fetch(`${API}/api/analysis-log`),
        fetch(`${API}/api/pipeline-scores`),
        fetch(`${API}/api/timer-v2-setups`),
      ]);
      if (logRes.ok) {
        const d = await logRes.json();
        setAnalysisLog(d.signals || []);
      }
      if (psRes.ok) {
        const d = await psRes.json();
        setPipelineScores(d.scores || []);
      }
      if (tv2Res.ok) {
        const d = await tv2Res.json();
        setTimerV2Setups(d.setups || []);
        setTimerV2Counts({ hold: d.hold || 0, fired: d.fired || 0 });
      }
    } catch (_) {}
    finally {
      if (showSpinner) setLoadingA(false);
      setAnalysisLoaded(true);
    }
  }, []);

  // Open SSE connection when on analysis tab; close when leaving.
  // SSE fires instantly on new signal; 30s poll is a fallback for when SSE is
  // blocked (CORS, mixed HTTP/HTTPS, proxy buffering) and also keeps pipeline scores fresh.
  useEffect(() => {
    if (activeTab !== "analysis") return;
    fetchAnalysis(!analysisLoaded);          // initial load (spinner on first visit)

    // SSE — sub-second update when a raw-log signal is saved to DB
    let es = null;
    try {
      es = new EventSource(`${API}/api/analysis-log/events`);
      es.addEventListener("new_signal", () => fetchAnalysis(false));
      // Re-fetch on open too — catches any signal saved during the connection handshake
      es.addEventListener("open", () => fetchAnalysis(false));
    } catch (_) {}

    // 30s fallback poll — resilience if SSE is blocked, also refreshes pipeline scores
    const id = setInterval(() => fetchAnalysis(false), 30_000);

    return () => {
      if (es) es.close();
      clearInterval(id);
    };
  // eslint-disable-next-line
  }, [activeTab]);

  // Fetch shadow A/B pairs (called on tab open and on every SSE event).
  const fetchShadow = useCallback(async (showSpinner) => {
    if (showSpinner) setLoadingS(true);
    try {
      const res = await fetch(`${API}/api/shadow`);
      if (res.ok) {
        const d = await res.json();
        setShadowPairs(d.pairs || []);
        setShadowSummary(d.summary || {});
        setShadowPerTrigger(d.per_trigger || {});
      }
    } catch (_) {}
    finally {
      if (showSpinner) setLoadingS(false);
      setShadowLoaded(true);
    }
  }, []);

  // Load shadow tab (SSE new_signal + 30s poll, same as analysis log).
  useEffect(() => {
    if (activeTab !== "shadow") return;
    fetchShadow(!shadowLoaded);
    let es = null;
    try {
      es = new EventSource(`${API}/api/analysis-log/events`);
      es.addEventListener("new_signal", () => fetchShadow(false));
      es.addEventListener("open", () => fetchShadow(false));
    } catch (_) {}
    const id = setInterval(() => fetchShadow(false), 30_000);
    return () => {
      if (es) es.close();
      clearInterval(id);
    };
  // eslint-disable-next-line
  }, [activeTab]);

  // Fetch pivot + swing levels (called on tab open, then polled).
  const fetchLevels = useCallback(async (showSpinner) => {
    if (showSpinner) setLoadingL(true);
    try {
      const [statusRes, swingRes] = await Promise.all([
        fetch(`${API}/api/status`),
        fetch(`${API}/api/swing-levels`),
      ]);
      if (statusRes.ok) {
        const d = await statusRes.json();
        setPivots(d.daily_pivots || {});
        setPivotWatchlist(d.watchlist || []);
      }
      if (swingRes.ok) {
        const d = await swingRes.json();
        setSwingLevels(d.levels || {});
        setSwingSymbols(d.symbols || []);
      }
    } catch (_) {}
    finally {
      if (showSpinner) setLoadingL(false);
      setLevelsLoaded(true);
    }
  }, []);

  // Load levels tab. Swing anchors move intraday, so poll every 30s while open.
  useEffect(() => {
    if (activeTab !== "levels") return;
    fetchLevels(!levelsLoaded);
    const id = setInterval(() => fetchLevels(false), 30_000);
    return () => clearInterval(id);
  // eslint-disable-next-line
  }, [activeTab]);

  const filteredJ  = applyFilter(signals,     filterJ, dateFromJ, dateToJ, null, symbolSearchJ, triggerFilterJ);
  const filteredA  = applyFilter(analysisLog, filterA, dateFromA, dateToA, typeFilterA, symbolSearchA, triggerFilterA);
  const outcomesJ  = outcomeBadges(signals);
  const outcomesA  = outcomeBadges(analysisLog);

  return (
    <div className="space-y-4">
      {/* Tab switcher */}
      <div className="flex gap-1 border-b border-gray-200">
        <button
          onClick={() => setActiveTab("journal")}
          className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
            activeTab === "journal"
              ? "bg-white text-blue-600 border-t border-l border-r border-gray-200 -mb-px"
              : "text-gray-400 hover:text-gray-600"
          }`}
        >
          Trade Journal
        </button>
        <button
          onClick={() => setActiveTab("analysis")}
          className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
            activeTab === "analysis"
              ? "bg-white text-blue-600 border-t border-l border-r border-gray-200 -mb-px"
              : "text-gray-400 hover:text-gray-600"
          }`}
        >
          Analysis Log
        </button>
        <button
          onClick={() => setActiveTab("shadow")}
          className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
            activeTab === "shadow"
              ? "bg-white text-purple-600 border-t border-l border-r border-gray-200 -mb-px"
              : "text-gray-400 hover:text-gray-600"
          }`}
        >
          Shadow A/B
        </button>
        <button
          onClick={() => setActiveTab("levels")}
          className={`px-4 py-2 text-sm font-medium rounded-t-lg transition-colors ${
            activeTab === "levels"
              ? "bg-white text-amber-600 border-t border-l border-r border-gray-200 -mb-px"
              : "text-gray-400 hover:text-gray-600"
          }`}
        >
          Levels
        </button>
      </div>

      {activeTab === "journal" ? (
        <>
          <StatsBar signals={filteredJ} />
          <FilterRow
            filter={filterJ} setFilter={setFilterJ}
            dateFrom={dateFromJ} setDateFrom={setDateFromJ}
            dateTo={dateToJ} setDateTo={setDateToJ}
            outcomes={outcomesJ} total={signals.length}
            symbolSearch={symbolSearchJ} setSymbolSearch={setSymbolSearchJ}
            triggerFilter={triggerFilterJ} setTriggerFilter={setTriggerFilterJ}
          />
          <SignalTable signals={filteredJ} loading={loadingJ} />
        </>
      ) : activeTab === "shadow" ? (
        <ShadowPanel
          pairs={shadowPairs}
          summary={shadowSummary}
          perTrigger={shadowPerTrigger}
          loading={loadingS && !shadowLoaded}
        />
      ) : activeTab === "levels" ? (
        <>
          <PivotLevelsTable pivots={pivots} watchlist={pivotWatchlist} loading={loadingL && !levelsLoaded} />
          <SwingLevelsTable levels={swingLevels} symbols={swingSymbols} loading={loadingL && !levelsLoaded} />
        </>
      ) : (
        <>
          <div className="bg-blue-50 border border-blue-200 rounded-lg px-4 py-2 text-xs text-blue-600">
            Shadow tracking — signals analyzed below Telegram threshold. Outcomes computed automatically. No alerts sent.
          </div>
          <StatsBar signals={filteredA} />
          <AnalysisFilterRow
            filter={filterA} setFilter={setFilterA}
            dateFrom={dateFromA} setDateFrom={setDateFromA}
            dateTo={dateToA} setDateTo={setDateToA}
            outcomes={outcomesA} total={analysisLog.length}
            typeFilter={typeFilterA} setTypeFilter={setTypeFilterA}
            symbolSearch={symbolSearchA} setSymbolSearch={setSymbolSearchA}
            triggerFilter={triggerFilterA} setTriggerFilter={setTriggerFilterA}
          />
          <SignalTable signals={filteredA} loading={loadingA} showReason={true} />
          <TimerV2Setups
            setups={timerV2Setups}
            hold={timerV2Counts.hold}
            fired={timerV2Counts.fired}
          />
          <PipelineScores scores={pipelineScores} />
        </>
      )}
    </div>
  );
}

